import { createContext, type PropsWithChildren, useCallback, useContext, useMemo, useState } from 'react';

import { get as storageGet, set as storageSet } from '../common/storage';
import { defaultPreferences, type Preferences, type ThemeMode, type ViewMode } from './models';

const PREFS_KEY = 'nuxni-time-tracker:preferences';

const loadPreferences = (): Preferences => {
    const raw = storageGet<Partial<Preferences>>(PREFS_KEY);
    if (!raw) {
        return defaultPreferences;
    }
    return {
        defaultView: raw.defaultView === 'list' ? 'list' : 'grid',
        themeMode: raw.themeMode === 'dark' ? 'dark' : 'light',
    };
};

const savePreferences = (next: Preferences): void => storageSet(PREFS_KEY, next);

type PreferencesContextValue = {
    preferences: Preferences;
    setDefaultView: (next: ViewMode) => void;
    setThemeMode: (next: ThemeMode) => void;
};

const PreferencesContext = createContext<PreferencesContextValue | undefined>(undefined);

export const PreferencesProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const [preferences, setPreferences] = useState<Preferences>(() => loadPreferences());

    const setDefaultView = useCallback((defaultView: ViewMode) => {
        setPreferences((prev) => {
            const next = { ...prev, defaultView };
            savePreferences(next);
            return next;
        });
    }, []);

    const setThemeMode = useCallback((themeMode: ThemeMode) => {
        setPreferences((prev) => {
            const next = { ...prev, themeMode };
            savePreferences(next);
            return next;
        });
    }, []);

    const value = useMemo<PreferencesContextValue>(
        () => ({ preferences, setDefaultView, setThemeMode }),
        [preferences, setDefaultView, setThemeMode],
    );

    return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
};

export const usePreferences = (): PreferencesContextValue => {
    const ctx = useContext(PreferencesContext);
    if (!ctx) {
        throw new Error('usePreferences must be used within a PreferencesProvider');
    }
    return ctx;
};
